import React, { createContext, useContext } from "react";
import type { ActivityNode, NoteNode } from "./activity-types";
import type { Omittable } from "@salestrak/ui";

export type ActivityContextValue = Omittable<{
  activities: ActivityNode[];
  getNote: (noteId: string) => NoteNode | undefined;
  loadMoreActivities: () => Promise<boolean | undefined>;
  hasMoreActivities: boolean;
  loadingMore: boolean;
  /** Distinct YYYY-MM-DD days the issue has feed-visible activity on,
   *  sorted descending. Used by the jump-to-date popover to enable options
   *  and pick a target day without paginating. */
  activityDates: string[];
  scrollToNote?: (noteId: string, parentNoteId?: string | null) => Promise<void>;
  // Selected note tracking for scroll-to + highlight
  selectedNoteId: string | null;
  selectedParentId: string | null;
  registerNoteRef: (noteId: string, el: any) => void;
  /** Mark a note read once it has been seen (scrolled into view). */
  markNoteRead: (noteId: string) => void;
  createNote: (input: {
    note: string;
    issueId: string;
    parentNoteId?: string | null;
    mediaIds?: string[] | null;
    inlineMediaIds?: string[] | null;
  }) => Promise<any>;
  updateNote: (noteId: string, content: string) => Promise<any>;
  deleteNote: (noteId: string) => Promise<any>;
  toggleReaction: (noteId: string, emoji: string) => Promise<void>;
  currentUserId: string | null;
  focusActivityId?: string | null;
  focusNoteId?: string | null;
  clearFocusActivity?: () => void;
}>;

const ActivityContext = createContext<ActivityContextValue | null>(null);

export function ActivityProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: ActivityContextValue;
}) {
  return (
    <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>
  );
}

export function useActivityContext(
  componentName: string = "Component",
): ActivityContextValue {
  const ctx = useContext(ActivityContext);
  if (!ctx) {
    throw new Error(
      `[useActivityContext] ${componentName} must be within ActivityProvider`,
    );
  }
  return ctx;
}

export function useOptionalActivityContext(): ActivityContextValue | null {
  return useContext(ActivityContext);
}
